import { useCallback, useEffect, useRef } from 'react'
import { useMap } from '@vis.gl/react-google-maps'
import { AUTO_FIT_PADDING_PX, MAP_CONFIG } from '../Map.data'

export interface CameraTarget {
  center: google.maps.LatLngLiteral
  zoom: number
}

const WORLD_TILE_PX = 256
const FLY_DURATION_MS = 1600

const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)

const lerp = (from: number, to: number, t: number) => from + (to - from) * t

const latRadians = (lat: number) => {
  const sin = Math.sin((lat * Math.PI) / 180)
  return Math.log((1 + sin) / (1 - sin)) / 2
}

/**
 * The camera position (center + zoom) that frames the given points — a fixed
 * focus zoom for a single point, or a bounds-fitting zoom for several.
 */
export function cameraForLocations(
  map: google.maps.Map,
  points: google.maps.LatLngLiteral[],
): CameraTarget | null {
  if (points.length === 0) return null
  if (points.length === 1) return { center: points[0], zoom: MAP_CONFIG.focusZoom }

  const bounds = new google.maps.LatLngBounds()
  points.forEach((point) => bounds.extend(point))
  const ne = bounds.getNorthEast()
  const sw = bounds.getSouthWest()

  const div = map.getDiv()
  const width = Math.max(1, div.clientWidth - AUTO_FIT_PADDING_PX * 2)
  const height = Math.max(1, div.clientHeight - AUTO_FIT_PADDING_PX * 2)

  const latFraction = Math.max(1e-8, (latRadians(ne.lat()) - latRadians(sw.lat())) / (2 * Math.PI))
  const lngDelta = ne.lng() - sw.lng()
  const lngFraction = Math.max(1e-8, (lngDelta < 0 ? lngDelta + 360 : lngDelta) / 360)

  // fitMaxZoom caps bounds fitting, so nearby pin pairs (hotel → venue) frame
  // as a strip-scale route view rather than zooming to street level.
  const zoom = Math.min(
    Math.log2(height / WORLD_TILE_PX / latFraction),
    Math.log2(width / WORLD_TILE_PX / lngFraction),
    MAP_CONFIG.fitMaxZoom,
  )

  return {
    center: bounds.getCenter().toJSON(),
    zoom: Math.max(MAP_CONFIG.minZoom, zoom),
  }
}

/**
 * Smoothly flies the map camera to a target — easing the pan while dipping
 * the zoom out and back in on long hops, like a flight path.
 */
export default function useFlyTo() {
  const map = useMap()
  const frameRef = useRef<number | undefined>(undefined)

  useEffect(
    () => () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current)
    },
    [],
  )

  return useCallback(
    (target: CameraTarget) => {
      if (!map) return
      if (frameRef.current) cancelAnimationFrame(frameRef.current)

      const startCenter = map.getCenter()?.toJSON()
      const startZoom = map.getZoom()
      if (!startCenter || startZoom === undefined) {
        map.moveCamera({ center: target.center, zoom: target.zoom })
        return
      }

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        map.moveCamera({ center: target.center, zoom: target.zoom })
        return
      }

      // On long hops, pull the camera up mid-flight so the pan reads as travel
      // rather than a smear across the map. (No antimeridian handling — the
      // mocked catalog spans the Atlantic, not the Pacific.)
      const moveDegrees = Math.hypot(
        target.center.lat - startCenter.lat,
        target.center.lng - startCenter.lng,
      )
      const zoomDip = Math.min(3, moveDegrees / 6)

      const startedAt = performance.now()
      const frame = (now: number) => {
        const t = Math.min(1, (now - startedAt) / FLY_DURATION_MS)
        const eased = easeInOutCubic(t)
        map.moveCamera({
          center: {
            lat: lerp(startCenter.lat, target.center.lat, eased),
            lng: lerp(startCenter.lng, target.center.lng, eased),
          },
          zoom: lerp(startZoom, target.zoom, eased) - zoomDip * Math.sin(Math.PI * t),
        })
        if (t < 1) frameRef.current = requestAnimationFrame(frame)
      }
      frameRef.current = requestAnimationFrame(frame)
    },
    [map],
  )
}
